/**
 * Locally generated call-progress tones (Web Audio), so there are no audio files to load and
 * nothing can 404. Cadences follow the Australian network where it matters (ringback).
 */

type Segment = [onSeconds: number, offSeconds: number];

interface Pattern {
  freqs: number[];
  cadence: Segment[];
  gain: number;
  type?: OscillatorType;
}

const PATTERNS = {
  // AU ringback: 400+425+450 Hz, 0.4 on 0.2 off 0.4 on 2.0 off
  ringback: { freqs: [400, 425, 450], cadence: [[0.4, 0.2], [0.4, 2.0]], gain: 0.08 },
  // Incoming ring: soft double-ring
  ringtone: { freqs: [523.25, 659.25], cadence: [[0.4, 0.2], [0.4, 2.0]], gain: 0.18, type: 'triangle' },
  // Call-waiting beep while already on a call
  waiting: { freqs: [425], cadence: [[0.2, 0.2], [0.2, 4.4]], gain: 0.06 },
} satisfies Record<string, Pattern>;

export type ToneName = keyof typeof PATTERNS;

const DTMF: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

class ToneEngine {
  private ctx: AudioContext | null = null;
  private current: { name: ToneName; stop: () => void } | null = null;
  private sinkId = '';

  private context(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext({ latencyHint: 'interactive' });
      this.applySink();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  /** Call from any user gesture so later (non-gesture) rings are allowed to play. */
  unlock(): void {
    this.context();
  }

  setSinkId(id: string): void {
    this.sinkId = id;
    this.applySink();
  }

  private applySink(): void {
    const ctx = this.ctx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (ctx?.setSinkId) ctx.setSinkId(this.sinkId).catch(() => undefined);
  }

  get playing(): ToneName | null {
    return this.current?.name ?? null;
  }

  play(name: ToneName): void {
    if (this.current?.name === name) return;
    this.stop();
    const ctx = this.context();
    const pattern: Pattern = PATTERNS[name];
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(ctx.destination);
    const oscs = pattern.freqs.map((f) => {
      const o = ctx.createOscillator();
      o.type = pattern.type ?? 'sine';
      o.frequency.value = f;
      o.connect(out);
      o.start();
      return o;
    });

    const level = pattern.gain / pattern.freqs.length;
    const cycle = pattern.cadence.reduce((t, [on, off]) => t + on + off, 0);
    let next = ctx.currentTime + 0.02;
    const schedule = () => {
      // keep ~2 cycles scheduled ahead
      while (next < ctx.currentTime + cycle * 2) {
        let t = next;
        for (const [on, off] of pattern.cadence) {
          out.gain.setValueAtTime(0, t);
          out.gain.linearRampToValueAtTime(level, t + 0.01);
          out.gain.setValueAtTime(level, t + on - 0.01);
          out.gain.linearRampToValueAtTime(0, t + on);
          t += on + off;
        }
        next = t;
      }
    };
    schedule();
    const timer = window.setInterval(schedule, 500);

    this.current = {
      name,
      stop: () => {
        window.clearInterval(timer);
        out.gain.cancelScheduledValues(ctx.currentTime);
        out.gain.setValueAtTime(0, ctx.currentTime);
        oscs.forEach((o) => o.stop(ctx.currentTime + 0.05));
        window.setTimeout(() => out.disconnect(), 100);
      },
    };
  }

  stop(name?: ToneName): void {
    if (!this.current || (name && this.current.name !== name)) return;
    this.current.stop();
    this.current = null;
  }

  /** Short local feedback when pressing keypad digits. */
  dtmf(digit: string): void {
    const pair = DTMF[digit];
    if (!pair) return;
    const ctx = this.context();
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.04, t);
    g.gain.setValueAtTime(0.04, t + 0.09);
    g.gain.linearRampToValueAtTime(0, t + 0.1);
    g.connect(ctx.destination);
    for (const f of pair) {
      const o = ctx.createOscillator();
      o.frequency.value = f;
      o.connect(g);
      o.start(t);
      o.stop(t + 0.11);
    }
    window.setTimeout(() => g.disconnect(), 200);
  }

  /** Three short beeps, used when a call fails or the far end hangs up. */
  endBeep(): void {
    const ctx = this.context();
    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(ctx.destination);
    const o = ctx.createOscillator();
    o.frequency.value = 425;
    o.connect(g);
    const t = ctx.currentTime + 0.02;
    for (let i = 0; i < 3; i++) {
      const s = t + i * 0.3;
      g.gain.setValueAtTime(0.05, s);
      g.gain.setValueAtTime(0, s + 0.15);
    }
    o.start(t);
    o.stop(t + 0.95);
    window.setTimeout(() => g.disconnect(), 1100);
  }
}

export const tones = new ToneEngine();
